/**
 * Buy-side (inventory / purchasing) CRM lead. Separate from sales {@link Lead}.
 * Stored in Firestore collection `inventory_leads`.
 */

import type { AppliedEstimatedRepairAddon } from "../expenses/expenseSettings";

export type InventoryLeadStatus =
  | "pendingList"
  | "fleetConfirmation"
  | "priceNegotiation"
  | "purchasing"
  | "availableNotPurchased"
  | "won"
  | "lost";

/**
 * Default board column order. `fleetConfirmation` remains a valid status for
 * legacy leads but is no longer shown as a default column.
 */
export const INVENTORY_LEAD_DEFAULT_STATUSES: InventoryLeadStatus[] = [
  "pendingList",
  "priceNegotiation",
  "availableNotPurchased",
  "purchasing",
  "won",
  "lost",
];

/** All known inventory lead statuses (including legacy columns). */
export const INVENTORY_LEAD_KNOWN_STATUSES: InventoryLeadStatus[] = [
  "pendingList",
  "fleetConfirmation",
  "priceNegotiation",
  "availableNotPurchased",
  "purchasing",
  "won",
  "lost",
];

export interface InventoryLeadTruck {
  make: string;
  model: string;
  year: number;
  vin?: string;
  size: string;
  type: string;
  fuel: "Gas" | "Diesel";
  /** Odometer reading — used for Gray Book retail valuation. */
  mileage?: number;
  /** Seller's asking price for this truck. */
  askingPrice?: number;
  /**
   * Optional paint color for Gray Book (defaults to White when valuing).
   * Stored as a free-form string to match inventory vehicle colors.
   */
  color?: string;
  /**
   * Whether the truck has a lien (private-party sellers). When true, lien
   * detail fields below mirror VehicleForm private-party lien inputs.
   */
  hasLien?: boolean | null;
  lienLender?: string | null;
  lienPayoffAmount?: number | null;
  /** ISO date string — same shape as Vehicle.lien_good_through_date. */
  lienGoodThroughDate?: string | null;
  lienAccountNumber?: string | null;
  /** Firebase Storage paths for photos of this truck. */
  photoStoragePaths?: string[];
  /**
   * Where we'd pick this truck up — a `pickup_locations` doc id from the
   * company's `dropoffLocationIds` list.
   */
  pickupLocationId?: string | null;
  /**
   * Repair shop we'd ship this truck to after purchase — a
   * `repair_shop_locations` doc id.
   */
  repairShopLocationId?: string | null;
  /** Driving miles from pickup to the saved repair shop. */
  shippingMiles?: number | null;
  /** shippingMiles × lead shippingPricePerMile when saved from the shipping tab. */
  shippingCost?: number | null;
  /** Estimated body repair cost; part of estimated_repair_cost total. */
  estimated_body?: number | null;
  /** Estimated mechanical repair cost; part of estimated_repair_cost total. */
  estimated_mechanical?: number | null;
  /** Estimated safety repair cost; part of estimated_repair_cost total. */
  estimated_safety?: number | null;
  /** Sum of estimated_body + estimated_mechanical + estimated_safety. */
  estimated_repair_cost?: number | null;
  /** Expense-settings add-ons applied to estimated repair categories. */
  estimated_repair_addons?: AppliedEstimatedRepairAddon[];
}

export interface InventoryLeadStatusHistoryEntry {
  id: string;
  /** Status the lead was moved to at this point. */
  status: InventoryLeadStatus | string;
  changedBy: string;
  changedAt: Date;
}

export interface InventoryLeadNote {
  text: string;
  author: string;
  updatedAt: string;
}

/** Who moved the price in a negotiation entry. */
export type InventoryLeadNegotiationParty = "us" | "seller";

export interface InventoryLeadNegotiationEntry {
  id: string;
  /** Index into `trucks` this entry belongs to; null for lead-wide entries. */
  truckIndex: number | null;
  party: InventoryLeadNegotiationParty;
  /** Offer or counter amount discussed, when a number was named. */
  amount?: number | null;
  note?: string | null;
  createdAt: string;
  createdBy: string;
}

export interface InventoryLeadAttachment {
  id: string;
  storagePath: string;
  fileName: string;
  fileSize: number;
  uploadedAt: string;
  uploadedBy: string;
}

/** Admin-managed reason for marking an inventory lead as lost. */
export interface InventoryLeadLostReason {
  id: string;
  label: string;
  createdAt: string;
  createdBy: string;
}

export interface InventoryLead {
  id: string;
  /** Human-readable identifier, e.g. `IL26-00042`. Assigned once at creation. */
  leadNumber?: string | null;
  contactId: string;
  companyId: string;
  trucks: InventoryLeadTruck[];
  /** Current board column id (default status or custom column id). */
  status: InventoryLeadStatus | string | null;
  /** Appended on every status change; latest entry drives time-in-status. */
  statusHistory: InventoryLeadStatusHistoryEntry[];
  /** Portal user id of the inventory rep (a user with `department: "inventory"`). */
  assignedRepId?: string | null;
  /** Single team note for this lead (author + last-updated timestamp). */
  note?: InventoryLeadNote | null;
  /** Lead-level documents (contracts, titles, etc.). */
  attachments?: InventoryLeadAttachment[];
  /** Per-mile shipping rate saved from the shipping tab. */
  shippingPricePerMile?: number | null;
  /** Offers and counters logged from the negotiation tab, oldest first. */
  negotiationHistory?: InventoryLeadNegotiationEntry[];
  /** Firestore id of the selected lost reason (when status is lost). */
  lostReasonId?: string | null;
  /** Denormalized label of the lost reason for display without a join. */
  lostReasonLabel?: string | null;
  /** Optional free-text context captured when marking the lead lost. */
  lostReasonNotes?: string | null;
  createdBy?: string;
  createdAt: Date;
  updatedAt: Date;
}
